import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { Option } from '@connect-x-app/shared-types';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, Modal, ModalOpener, SearchableSelect } from '@connect-x-app/web-ui';

import styles from './MuteFlightroomModal.module.scss';

type MuteFlightroomModalProps = {
  onDismiss: () => void;
  onSelect: (duration: string) => void;
};

const OPTIONS: Option[] = Array.from({ length: 24 }, (_, i) => i + 1).map(hour => {
  return { label: `${hour}`, value: `${hour}` };
});

export const MuteFlightroomModal = ({ onDismiss, onSelect }: MuteFlightroomModalProps) => {
  const { t } = useTranslation();
  const [duration, setDuration] = useState('');

  const isValid = useMemo(() => {
    return !isNaN(+duration) && +duration >= 1 && +duration <= 24;
  }, [duration]);

  const onSubmit = () => {
    onSelect(duration);
    ModalOpener.instance.close();
  };

  const onCancel = () => {
    onDismiss();
    ModalOpener.instance.close();
  };

  const onChange = (option: SingleValue<Option> | MultiValue<Option>) => {
    if (option && 'value' in option) {
      setDuration(option.value);
    } else {
      setDuration('');
    }
  };

  const selectedOptions: Option[] = useMemo(() => {
    return duration ? [{ label: duration, value: duration }] : [];
  }, [duration]);

  return (
    <Modal onCancel={onDismiss} testId={FLIGHTROOM_TEST_IDS.muteModal.modal}>
      <Modal.Header onCancel={onCancel}>{t('FLIGHTROOMS.MUTE.MODAL.TITLE')}</Modal.Header>
      <Modal.Content>
        <SearchableSelect
          className={styles['duration-select']}
          closeMenuOnSelect
          isModal
          isMulti={false}
          isSearchable={false}
          label={t('FLIGHTROOMS.MUTE.MODAL.LABEL')}
          name={'duration'}
          onChange={onChange}
          options={OPTIONS}
          searchableSelectConfig={{ placeholder: t('FLIGHTROOMS.MUTE.MODAL.PLACEHOLDER') }}
          selectedOptions={selectedOptions}
          testId={FLIGHTROOM_TEST_IDS.muteModal.duration}
        />
      </Modal.Content>
      <Modal.Buttons>
        <Button isOutline onClick={onCancel} testId={FLIGHTROOM_TEST_IDS.muteModal.cancel} theme="negative">
          {t('SHARED.BUTTONS.CANCEL')}
        </Button>
        <Button disabled={!isValid} onClick={onSubmit} testId={FLIGHTROOM_TEST_IDS.muteModal.submit} theme="primary">
          {t('SHARED.BUTTONS.SUBMIT')}
        </Button>
      </Modal.Buttons>
    </Modal>
  );
};
